import React, { ReactElement, ReactText } from 'react';
import { View } from 'react-native';
import { Platform } from 'src/libs/platform';
import { Tappable, TappableProps } from 'src/components/Tappable/Tappable';
import { Headline } from 'src/components/Typography';
import { PlatformPadding } from 'src/components/PlatformPadding';
import CheckCircleIcon from 'src/assets/icons/custom/check-round.svg';
import ChevronRightIcon from 'src/assets/icons/chevron-right.svg';
import useStyles from './Cell.styles';

export type CellChangeFunc = (name: string, checked: boolean) => void;
export type CellProps = TappableProps & {
  checked?: boolean;
  name?: string;
  onChange?: CellChangeFunc;
  expandable?: boolean;
  before?: ReactElement;
  after?: ReactElement;
  indicator?: ReactText;
};

export const Cell: React.FC<CellProps> = ({
  children,
  before,
  after,
  onChange,
  checked,
  name,
  expandable,
  indicator,
  disabled,
  style,
  ...props
}) => {
  const s = useStyles();

  if (__DEV__) {
    if (!!onChange && name === undefined) {
      console.error('[@pulsar/ui]: selectable Cells must have "onChange" and "name" props');
    }
  }

  const handleChange = () => {
    onChange!(name!, !checked);
  };

  return (
    <Tappable
      style={[s.root, style]}
      hoverable={!disabled}
      borderless
      disabled={disabled}
      onPress={onChange ? handleChange : undefined}
      accessibilityRole={onChange ? 'checkbox' : 'button'}
      accessibilityState={{ disabled, checked }}
      {...props}
    >
      <PlatformPadding horizontal>
        <View style={s.cell}>
          {before && <View style={s.before}>{before}</View>}
          <Headline style={s.main} numberOfLines={1}>
            {children}
          </Headline>
          {indicator ? (
            <Headline style={s.indicator} numberOfLines={1}>
              {indicator}
            </Headline>
          ) : after ? (
            after
          ) : checked ? (
            <CheckCircleIcon style={s.icon} />
          ) : undefined}
          {expandable && Platform.OS === 'ios' && <ChevronRightIcon style={s.expandableIcon} />}
        </View>
      </PlatformPadding>
    </Tappable>
  );
};
